import { Injectable } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { WorkflowService } from '../workflow/workflow.service';
import { AmorphicService } from '../amorphic/amorphic.service';
import { WorkflowHelperInterface } from '../../interfaces/helper/WorkflowHelperInterface';
import { Policy } from '../../../../common/js/customer/Policy';
import { Router } from '@angular/router';
import { CoverpathUtils } from '../../../js/static/CoverpathUtils';
import {
  CACPostRiderDTO,
  Exchange1035FundingPurpose,
  Exchange1035FundingStatus,
  LockedByUserType,
  ModalType,
} from '../../../js/interfaces/CoverpathTypes';
import { ModalService } from '../modal/modal.service';
import { CasApplication } from '../../../classes/coverpath_subclasses/CasApplication';
import { Utils } from '../../../../common/js/Utils';
import moment from 'moment';
import { take } from 'rxjs/operators';
import { MMDirectApplication } from '../../../../coverpath-common/classes/mmdirect_subclasses/MMDirectApplication';
import { CoverpathPolicy } from '../../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { MMDirectPolicy } from '../../../../coverpath-common/classes/mmdirect_subclasses/MMDirectPolicy';
import { CoverpathControllerInterface } from '../../interfaces/controller/CoverpathControllerInterface';
import {
  IsLoadingDataInterface,
  IsLoadingType,
} from '../../../../coverpath-common/angular/interfaces/account-center/IsLoadingDataInterface';
import { InitialUIPayload, LifePolicyInfoDTO } from '@hit/policy-admin-types/dist/data-model/Types';
import { BenefitCode } from '@hit/policy-admin-types/dist/data-model/ProductApiTypes';
import { RiderType } from '../../../../common/js/static/Product';
import { CACPaymentData } from '../../interfaces/CACPaymentData';
import { PolicyData } from '../../interfaces/PolicyData';
import { CashValueQuote } from '../../../../common/js/policyAdmin/PolicyAdminClient';
import {
  Rider,
  QuoteExchange1035,
} from '../../../../coverpath-agent/public/js/components/home-new/quote-or-apply-new/QuoteForm';
import { UICategory, UILoggerSingleton } from '../../../../common/js/logger/UILoggerSingleton';
import { ClientReapplyResponseType } from '../../../../coverpath-common/js/interfaces/CoverpathTypes';

@Injectable()
export class AccountCenterService {
  static readonly UNKNOWN_ACTION_STRING = '';

  primaryPolicy: CoverpathPolicy | MMDirectPolicy;
  secondaryPolicy: CoverpathPolicy | MMDirectPolicy;
  application: CasApplication | MMDirectApplication;

  POLL_INTERVAL_ACTION_STATUS: number = 3000;
  POLL_INTERVAL_SHORT: number = 10000;
  POLL_INTERVAL_LONG: number = 30000;
  UW_DELAY: number = 30000;
  PAYMENT_FAILURE_STATUS: string = 'PaymentFailure';

  workflowStateSubject: ReplaySubject<string> = new ReplaySubject<string>(1);
  isLoadingSubject: Subject<IsLoadingDataInterface> = new Subject<IsLoadingDataInterface>();
  latestActionString = AccountCenterService.UNKNOWN_ACTION_STRING;
  workflowService: WorkflowService;
  amorphicService: AmorphicService;
  modalService: ModalService;
  controller: CoverpathControllerInterface;
  bannerTitles: string[];
  bannerSubTitles: string[];
  policyNumberPolicyDataMap: Map<string, PolicyData> = new Map();

  workflowHelper: WorkflowHelperInterface;

  //how often do we call dispatchStatus()?
  pollInterval: number;

  //the timer obj returned by setInterval()... needed to reset/stop the the currently running interval
  actionLoopInterval;

  //are we in the heart of getActionString()?
  inGetActionStringLock: boolean;

  // set when a click waits on the next backend stage, where futureState is not dependable enough e.g. cancel/reapply
  actionPolling: boolean = false;
  private workflowStateSubjectSubcriber;

  constructor(
    workflowService: WorkflowService,
    amorphicService: AmorphicService,
    modalService: ModalService,
    private router: Router
  ) {
    this.amorphicService = amorphicService;
    this.workflowService = workflowService;
    this.modalService = modalService;
    this.controller = this.amorphicService.controller;
    this.workflowHelper = this.amorphicService.controller.workflowHelper;
  }

  async start() {
    await this.controller.accountCenterController.setupPollConfigs();
    this.POLL_INTERVAL_SHORT = this.controller.accountCenterController.shortPoll || this.POLL_INTERVAL_SHORT;
    this.POLL_INTERVAL_LONG = this.controller.accountCenterController.longPoll || this.POLL_INTERVAL_LONG;
    this.POLL_INTERVAL_ACTION_STATUS =
      this.controller.accountCenterController.actionStatusPoll || this.POLL_INTERVAL_ACTION_STATUS;
    this.UW_DELAY = this.controller.accountCenterController.delay;
    await this.dispatchStatus();
  }

  // update the poll duration to be <pollInterval>
  // stop the current and reset the interval if different
  actionLoopSetPollDuration(pollInterval: number): void {
    if (!this.actionLoopInterval || this.pollInterval !== pollInterval) {
      this.actionLoopStop();
      this.actionLoopStart(pollInterval);
    }
  }

  actionLoopStart(pollInterval: number): void {
    //double-check that any previous running interval has been cleared, otherwise we end up with multiple intervals running
    if (this.actionLoopInterval) {
      this.actionLoopStop();
    }

    this.pollInterval = pollInterval;
    this.actionLoopInterval = setInterval(() => this.dispatchStatus(), pollInterval);
  }

  actionLoopStop(): void {
    clearInterval(this.actionLoopInterval);
    this.actionLoopInterval = null;
    this.latestActionString = AccountCenterService.UNKNOWN_ACTION_STRING;
    this.workflowStateSubject.next(AccountCenterService.UNKNOWN_ACTION_STRING);
  }

  private getAgentLicenseCheckResult(policy: Policy): Promise<string> {
    const activity: string = 'CS2A1 Verify agent license';
    return this.workflowHelper.getWorkflowActivityResult(policy, activity);
  }

  dispatchStatus() {
    if (this.inGetActionStringLock) {
      return Promise.resolve(this.latestActionString);
    }

    this.inGetActionStringLock = true;

    // used for logging error, if user leaves detail view, unsetSelectedApplication clears values that are used
    // this causes getActionString to throw errors, look at HL-21093 for more info
    let applicationNumber: string;
    if (this.application) {
      applicationNumber = this.application.applicationNumber;
    }

    return this.getActionString()
      .then((actionString) => {
        if (actionString !== this.latestActionString || CoverpathUtils.isNonUniqueAction(actionString)) {
          UILoggerSingleton.getInstance().info(
            {
              module: 'account-center.service',
              function: 'dispatchStatus',
              category: UICategory.request,
              data: {
                applicationNumber: this.application.applicationNumber,
                actionString,
                latestActionString: this.latestActionString,
              },
            },
            'Application status has changed'
          );

          this.workflowStateSubject.next(actionString);
          this.latestActionString = actionString;
          this.actionPolling = false;
        }

        this.inGetActionStringLock = false;
        return this.latestActionString;
      })
      .catch((e) => {
        // @NOTE we need this because of polling service issues, look at HL-21093 for more info
        this.inGetActionStringLock = false;

        const error = {
          isHumanRelated: false,
          name: e.name,
          message: e.message,
          stack: e.stack,
          data: {
            browseURL: window.location.href,
            sessionApplicationNumber:
              this.controller.customer.application && this.controller.customer.application.applicationNumber,
            accountCenterApplicationNumber: applicationNumber,
            latestActionString: this.latestActionString,
          },
        };
        UILoggerSingleton.getInstance().error(
          {
            module: 'account-center-service',
            function: 'dispatchStatus',
            category: UICategory.request,
            error: error,
          },
          'Failed to change Application status - usually happens when user is switching from detail view to list view'
        );
        return this.latestActionString;
      });
  }

  makeActionString(policy: Policy): string {
    const state = this.workflowService.getWorkflowStateFromPolicy(policy);
    const subState = this.workflowService.getWorkflowSubStateFromPolicy(policy);

    if (!state) {
      return 'Loading:loading';
    } else {
      return `${state}:${subState}`;
    }
  }

  async getActionString(): Promise<string> {
    if (!this.primaryPolicy) {
      return this.latestActionString;
    }

    let actionString: string;

    //force a browser/server sync
    await this.controller.publicSave();
    actionString = this.makeActionString(this.application.primaryPolicyWithPendingAmendment);

    await new Promise((r) =>
      setTimeout(() => {
        r(true);
      }, 3000)
    );
    //Note: this function short-circuits if the policy's workflow state/substate is such that it can't be in PA
    //As such, we actually aren't hitting PA every time
    const primaryPolicy = this.application.primaryPolicyWithPendingAmendment;
    const policyAdminStage = await this.controller.accountCenterController.getPAStatusWorkflowEquivalentStage(
      primaryPolicy.policyNumber
    );
    if (policyAdminStage) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'account-center.service',
          function: 'dispatchStatus',
          category: UICategory.request,
          data: {
            applicationNumber: this.application.applicationNumber,
            policyAdminStage,
          },
        },
        'Retrieved status from policy admin'
      );

      actionString = policyAdminStage;
    }

    const secondaryPolicy = this.application.secondaryPolicyWithPendingAmendment;
    if (secondaryPolicy) {
      const override = await this.getSecondaryPolicyStateOverride(actionString);
      if (override) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'account-center.service',
            function: 'dispatchStatus',
            category: UICategory.request,
            data: {
              applicationNumber: this.application.applicationNumber,
              override,
            },
          },
          'Got secondary policy status override'
        );

        actionString = override;
      }
    }

    actionString = this.overrideByAlias(actionString);

    //determine polling frequency for post-submission stages
    if (!this.actionPolling) {
      if (
        CoverpathUtils.isAcceptStage(actionString) ||
        CoverpathUtils.isInManualUnderwriting(actionString) ||
        CoverpathUtils.isIssuingPolicy(actionString) ||
        CoverpathUtils.isInFreeLook(actionString) ||
        CoverpathUtils.isCanceled(actionString)
      ) {
        this.actionLoopSetPollDuration(this.POLL_INTERVAL_LONG);
      } else if (CoverpathUtils.isAcceptOfferStage(actionString)) {
        this.actionLoopSetPollDuration(this.POLL_INTERVAL_ACTION_STATUS);
      } else {
        this.actionLoopSetPollDuration(this.POLL_INTERVAL_SHORT);
      }
    }

    this.inGetActionStringLock = false;

    UILoggerSingleton.getInstance().info(
      {
        module: 'account-center.service',
        function: 'dispatchStatus',
        category: UICategory.request,
        data: {
          applicationNumber: this.application.applicationNumber,
          actionString,
          pollInterval: this.pollInterval,
        },
      },
      'Fetched latest application status'
    );

    return actionString;
  }

  overrideByAlias(actionString: string) {
    const app = this.primaryPolicy.application;
    const esignMetadata = this.primaryPolicy.getOwnerSigningPerson().esignMetadata;
    const tlicToKOEsign = this.controller.accountCenterController.tlicToKOEsign;
    const isDocuSignRedirectEnabled = this.amorphicService.controller.isDocuSignRedirectEnabled;
    const ownerEsignComplete =
      (esignMetadata && esignMetadata.ownerSignComplete) || this.primaryPolicy.applicationSignedDate;
    const shouldAliasSignature =
      isDocuSignRedirectEnabled &&
      this.primaryPolicy.isCoverpathPolicy() &&
      !(app as CasApplication).docuSignLinkInProgress &&
      !ownerEsignComplete &&
      !tlicToKOEsign;
    this.controller.accountCenterController.tlicToKOEsign = false;
    switch (actionString) {
      case 'Accelerated Application:Waiting for Signature':
        if (shouldAliasSignature) {
          actionString = 'Accelerated Application:Waiting for Acceptance';
        }
        break;
      case 'Haven Application:Waiting for Signature':
        if (shouldAliasSignature) {
          actionString = 'Haven Application:Waiting for Application Acceptance';
        }
        break;
      case 'TLIC Application:Waiting for Signature':
        if (shouldAliasSignature) {
          actionString = 'TLIC Application:Waiting for Acceptance';
        }
        break;
    }
    return actionString;
  }

  private async getSecondaryPolicyStateOverride(actionString: string): Promise<string> {
    if ((await this.getAgentLicenseCheckResult(this.primaryPolicy)) === 'Invalid') {
      this.actionLoopStop();
      return `${actionString}:Invalid`;
    }

    const isPrimaryCanceledInPA = await this.controller.accountCenterController.isCanceledInPA(
      this.application.primaryPolicyWithPendingAmendment.policyNumber
    );

    //primary policy isn't canceled, so secondary policy state override is not applicable
    if (!isPrimaryCanceledInPA) {
      return null;
    }

    const isSecondaryCanceledInPA = await this.controller.accountCenterController.isCanceledInPA(
      this.application.secondaryPolicyWithPendingAmendment.policyNumber
    );
    if (isPrimaryCanceledInPA && !isSecondaryCanceledInPA) {
      return this.controller.accountCenterController.getPAStatusWorkflowEquivalentStage(
        this.secondaryPolicy.policyNumber
      );
    }

    return null;
  }

  /**
   * This has the side effect of forcing the account center detailed page to update with the latest (future) workflow state/substate
   */
  async forceWorkflowStateNotification(): Promise<void> {
    await this.controller.publicSave();
    const actionString = this.makeActionString(this.primaryPolicy);
    this.workflowStateSubject.next(actionString);
  }

  /**
   * There is currently no way to check on the PA side whether or not the annual statement is yet available,
   * it may not be if the effective date in their database was manually changed. Therefore this method is an
   * approximation
   */
  shouldHaveAnnualStatement(): boolean {
    const primaryPolicy = this.application.primaryPolicy;
    if (this.application.isJointApp() || primaryPolicy.isWholeLifePolicy()) {
      const oneYear = moment(primaryPolicy.effectiveDate).add(1, 'year');
      return moment().isSameOrAfter(oneYear);
    }

    return false;
  }

  async esignFromService(): Promise<void> {
    const app = this.controller.customer.application;
    const metadata = app.primaryPolicy.getOwnerSigningPerson().esignMetadata;
    // if there is a link it is probably outdated
    if (metadata && (metadata.linkValue || metadata.linkGeneratedAt)) {
      metadata.linkValue = null;
      metadata.linkGeneratedAt = null;
    }

    if (metadata && metadata.docsGeneratedAt) {
      await this.controller.accountCenterController.regenerateESignLink(this.primaryPolicy);
    } else {
      await this.controller.accountCenterController.checkAcceptApplicationReq(this.primaryPolicy);
    }

    if (app.primaryPolicy.isCoverpathPolicy()) {
      (app as CasApplication).docuSignLinkInProgress = true;
    }

    await this.dispatchStatus();
  }

  async acceptFromService(): Promise<void> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'account-center.service',
        function: 'acceptFromService',
        category: UICategory.request,
        data: {
          policyNumber: this.primaryPolicy.policyNumber,
          ...Utils.extractIds(this.primaryPolicy, this.controller.customer),
        },
      },
      'Account center Accept offer'
    );
    await this.controller.accountCenterController.acceptOffer(this.primaryPolicy);
    await this.dispatchStatus();
  }

  async acceptOfferPreviewFromService(): Promise<void> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'account-center.service',
        function: 'acceptOfferPreviewFromService',
        category: UICategory.request,
        data: {
          policyNumber: this.primaryPolicy.policyNumber,
          ...Utils.extractIds(this.primaryPolicy, this.controller.customer),
        },
      },
      'Account center Accept offer preview'
    );
    await this.controller.accountCenterController.acceptOfferPreview(this.primaryPolicy as CoverpathPolicy);
    this.dispatchStatus();
  }

  resendFromService(): void {
    this.controller.accountCenterController.resendDocusign(this.primaryPolicy);
  }

  resendInsuredEsignEmail(): void {
    this.controller.accountCenterController.resendInsuredEsignEmail(this.primaryPolicy);
  }

  cancelFromService(): void {
    this.controller.accountCenterController.cancelApplication(this.primaryPolicy);
    this.actionLoopSetPollDuration(this.POLL_INTERVAL_ACTION_STATUS);
    this.actionPolling = true;
  }

  async cancelAndReapply() {
    await this.controller.accountCenterController.cancelApplication(this.primaryPolicy);
    const subscription = this.workflowStateSubject.pipe(take(2)).subscribe(() => {
      if (this.primaryPolicy.hasEnded()) {
        subscription.unsubscribe();
        this.reapplyRouteToApplication();
      }
    });
  }

  async declineTlicOffer() {
    await this.controller.accountCenterController.declineTlicOffer(this.primaryPolicy.policyNumber);
  }

  async reapplyRouteToApplication(): Promise<ClientReapplyResponseType> {
    const attributesForOriginalAppAndPolicy = AccountCenterService.getAttributesForAppAndPolicy(
      this.controller.customer.application
    );

    //make it harder for the client to hit the back button and get back to a loading screen
    await this.router.navigate(['loading'], { skipLocationChange: true });
    this.actionLoopStop();
    if (this.controller.customer.isCoverpathCustomer()) {
      this.controller.applicationLockController.setApplicationLockedBy(LockedByUserType.CUSTOMER);
    }
    const successFromController = await this.controller.accountCenterController.reapplyApplication();

    //examine the new application and policy for sensible values
    const attributesForUpdatedAppAndPolicy = AccountCenterService.getAttributesForAppAndPolicy(
      this.controller.customer.application
    );
    const workflowStatesAreClear =
      !this.controller.customer.application.primaryPolicy.workflowState &&
      !this.controller.customer.application.primaryPolicy.workflowSubState;
    const policyNumberIsNotAssigned = !this.controller.customer.application.primaryPolicy.policyNumber;
    const newApplicationNumberIsHigher =
      Number(this.controller.customer.application.applicationNumber) >
      attributesForOriginalAppAndPolicy.applicationNumber;

    if (
      successFromController === ClientReapplyResponseType.SUCCESS &&
      workflowStatesAreClear &&
      policyNumberIsNotAssigned &&
      newApplicationNumberIsHigher
    ) {
      await this.router.navigate(['application-form']);
      return successFromController;
    } else {
      const error = {
        isHumanRelated: false,
        data: {
          attributesForOriginalAppAndPolicy,
          attributesForUpdatedAppAndPolicy,
        },
      };
      UILoggerSingleton.getInstance().error({
        module: 'account-center.service',
        function: 'reapplyRouteToApplication',
        category: UICategory.request,
        error: error,
      });

      await this.router.navigate(['account-center']);
      return successFromController;
    }
  }

  private static getAttributesForAppAndPolicy(application: CasApplication | MMDirectApplication) {
    return {
      workflowState: application.primaryPolicy.workflowState,
      workflowSubState: application.primaryPolicy.workflowSubState,
      policyNumber: application.primaryPolicy.policyNumber,
      applicationNumber: Number(application.applicationNumber),
    };
  }

  resendEmailVerification(): Promise<void> {
    return this.controller.accountCenterController.resendVerificationEmail();
  }

  activateDocumentUploadModal(): void {
    const modalConfig = {
      type: ModalType.documentUpload,
    };

    this.modalService.activate(modalConfig);
  }

  getApplicationWithNumber(
    applications: (CasApplication | MMDirectApplication)[],
    applicationNumber: string
  ): CasApplication | MMDirectApplication {
    if (!applicationNumber) {
      return null;
    }

    const matchingApplications = applications.filter(
      (application) => application.applicationNumber === applicationNumber
    );

    if (matchingApplications.length !== 1) {
      return null;
    }

    return matchingApplications[0];
  }

  /**
   * @throws Error if we do not have an application with given applicationNumber
   */
  async setSelectedApplication(requestedApplicationNumber: string, ownerDocusignComplete?: string): Promise<void> {
    if (this.application && this.application.applicationNumber !== requestedApplicationNumber) {
      this.unsetSelectedApplication();
    }

    // @TODO refactor once HL-21093 is done
    this.application = this.getApplicationWithNumber(
      this.controller.customer.activeApplications,
      requestedApplicationNumber
    );
    if (!this.application) {
      // @NOTE throw error because all functions/components that use this function won't work if application is not defined
      throw new Error(`Sorry, we could not select the application with this ${requestedApplicationNumber}`);
    }

    this.primaryPolicy = this.application.primaryPolicy;
    this.secondaryPolicy = this.application.secondaryPolicy;

    //to refresh the data on the CAC
    this.workflowStateSubjectSubcriber = this.workflowStateSubject.subscribe(async (workflowState) => {
      if (workflowState === AccountCenterService.UNKNOWN_ACTION_STRING) {
        return;
      }

      if (!this.application) {
        // @NOTE throw error because all functions/components that use this function won't work if application is not defined
        throw new Error(`Sorry, we could not select the application with this ${requestedApplicationNumber}`);
      }

      const primaryPolicy = this.application.primaryPolicy;
      const primaryPolicyWithPendingAmendment = this.application.primaryPolicyWithPendingAmendment;
      const secondaryPolicyWithPendingAmendment = this.application.secondaryPolicyWithPendingAmendment;
      const secondaryPolicy = this.application.secondaryPolicy;
      const policiesToFetchData = [];
      policiesToFetchData.push(primaryPolicyWithPendingAmendment);
      if (primaryPolicyWithPendingAmendment !== primaryPolicy) {
        policiesToFetchData.push(primaryPolicy);
      }

      if (secondaryPolicyWithPendingAmendment && secondaryPolicyWithPendingAmendment !== primaryPolicy) {
        policiesToFetchData.push(secondaryPolicyWithPendingAmendment);
      }

      if (secondaryPolicy && secondaryPolicy !== secondaryPolicyWithPendingAmendment) {
        policiesToFetchData.push(secondaryPolicy);
      }

      this.isLoadingSubject.next({
        isLoading: true,
        type: IsLoadingType.NORMAL,
        action: this.latestActionString,
      });

      await Promise.all(
        policiesToFetchData
          .filter((value, index, array) => array.indexOf(value) === index)
          .map(async (policy) => {
            this.setPreIssueDataPerPolicy(policy, policy.__id__);
            await this.getPostIssuePolicyData(policy);
          })
      );

      this.isLoadingSubject.next({
        isLoading: false,
        type: IsLoadingType.NORMAL,
        action: this.latestActionString,
      });
    });

    const esignMetadata = this.primaryPolicy.getOwnerSigningPerson().esignMetadata;
    if (ownerDocusignComplete && ownerDocusignComplete === '1') {
      // When redirecting from docusign and they tell us owner has signed, set that to be true manually.
      // If owner is redirected after signing docusign won't have updated its status to show owner
      // sign complete yet, leading to them seeing the wrong screen if this isn't done

      // If we've already hit this path, don't want to overwrite any updated values
      if (esignMetadata && !esignMetadata.ownerSignComplete) {
        esignMetadata.ownerSignComplete = true;
        if (
          (!this.primaryPolicy.insuredIsJuvenile() || this.primaryPolicy.isOwnerOtherThanSigner()) &&
          (this.primaryPolicy.isOwnerOtherThanInsured() || this.primaryPolicy.hasSecondOwner())
        ) {
          esignMetadata.waitingOnOtherSigners = true;
        }
      }
    }

    if (esignMetadata && esignMetadata.ownerSignComplete && esignMetadata.waitingOnOtherSigners) {
      await esignMetadata.setOwnerSignComplete();
    }

    // @TODO remove once HL-21093 is done
    this.controller.customer.application = this.application;
    await this.controller.publicSave();

    UILoggerSingleton.getInstance().info(
      {
        module: 'AccountCenterService',
        function: 'setSelectedApplication',
        category: UICategory.request,
        data: Utils.extractIds(this.controller.customer, this.controller.customer.application),
      },
      `Set current application with number ${this.application.applicationNumber}`
    );
  }

  notifyAdvisorOnceClientSignsConversion(policy: CoverpathPolicy) {
    return this.amorphicService.controller.accountCenterController.notifyAdvisorOnceClientSignsConversion(policy);
  }

  private async getPostIssuePolicyData(policy: CoverpathPolicy | MMDirectPolicy) {
    if (policy && policy.isIssued()) {
      const couldPolicyExistInPA = await this.controller.accountCenterController.isPAPolicy(policy.policyNumber);
      const paWorkflowStatus =
        await this.controller.accountCenterController.getPAStatusWorkflowEquivalentStageForCoverpath(
          policy.policyNumber
        );
      if (couldPolicyExistInPA) {
        const paPolicyDetails: InitialUIPayload = await this.controller.accountCenterController.getPAPolicy(policy);
        const paRiders: CACPostRiderDTO = await this.controller.accountCenterController.getInsuranceSegmentRiderInfo(
          policy
        );
        let paPayerInfo, paInvoices, paRecentInvoiceInfo, paCreatedNoAuthInvoiceDate;
        if (this.controller.enableAuthPayforDDB) {
          paPayerInfo = await this.controller.accountCenterController.getPAPayerInfo(policy);
          paInvoices = await this.controller.accountCenterController.getPAInvoices(policy);
          paCreatedNoAuthInvoiceDate = this.paCreatedNoAuthInvoiceDate(paInvoices);
        }
        if (this.controller.enableFailedPaymentRetry) {
          paRecentInvoiceInfo = await this.extractRecentInvoiceInfo(policy);
        }

        if (paPolicyDetails) {
          paPolicyDetails.policy = JSON.parse(paPolicyDetails.policy);
          paPolicyDetails.invoices = paPolicyDetails.invoices.map((invoice) => JSON.parse(invoice));
          paPolicyDetails.activeSegments = paPolicyDetails.activeSegments.map((activeSegment) =>
            JSON.parse(activeSegment)
          );
          paPolicyDetails.paymentInfo.paymentAmount = JSON.parse(paPolicyDetails.paymentInfo.paymentAmount);
          paPolicyDetails.paymentInfo.nextPaymentDate = JSON.parse(paPolicyDetails.paymentInfo.nextPaymentDate);

          const paCACPaymentData = this.setPAPolicyDetails(
            paPolicyDetails,
            policy,
            paPayerInfo,
            paRecentInvoiceInfo,
            paCreatedNoAuthInvoiceDate
          );
          const policyData = this.policyNumberPolicyDataMap.get(policy.__id__ as string);
          policyData.overrideFromPAPolicy(paPolicyDetails, paWorkflowStatus);
          policyData.setCACPaymentData(paCACPaymentData);
          if (paRiders) {
            policyData.setCACIncludeAdbr(paRiders.adbr);
            policyData.setCACWavierOfPremium(paRiders.waiverOfPremium);
          }
          this.policyNumberPolicyDataMap.set(policy.__id__ as string, policyData);
        }
      }
    }
  }

  unsetSelectedApplication() {
    this.application = this.primaryPolicy = this.secondaryPolicy = null;

    //TODO - Should we uns et _activeApplication on the customer? https://massmutual.atlassian.net/browse/HL-19070

    if (this.workflowStateSubjectSubcriber) {
      this.workflowStateSubjectSubcriber.unsubscribe();
    }
  }

  async setPolicyViewDataForListView() {
    const applications: Array<CasApplication | MMDirectApplication> = this.controller.customer.activeApplications;
    const allPolicies = applications.reduce((acc, app) => {
      acc = acc.concat([app.primaryPolicyWithPendingAmendment]);
      return acc.concat(app.isJointAppWithPendingAmendment() ? [app.secondaryPolicyWithPendingAmendment] : []);
    }, []);

    const policyIds = allPolicies.map((policy) => policy.__id__);
    (policyIds || []).forEach((policyId) => {
      const currentPolicy = allPolicies.find((policy) => policy.__id__ === policyId);
      this.setPreIssueDataPerPolicy(currentPolicy, policyId);
    });

    await this.setPAPolicyInfo(allPolicies);
  }

  private async setPAPolicyInfo(allPolicies) {
    if (allPolicies.length && this.controller.customer.isCoverpathCustomer()) {
      const policyNumbers = allPolicies.map((policy) => policy.policyNumber);
      const policyInfos = await this.controller.accountCenterController.getPAPolicyInfoBatch(policyNumbers);
      const postIssueDataPromises = [];
      for (const policyInfoPayload of (policyInfos || []) as LifePolicyInfoDTO[]) {
        const policy = allPolicies.find((policy) => policy.policyNumber === policyInfoPayload.policyInfo.policyNumber);
        //Only get data for post issued policies, because TLIC policies will override amendments data
        if (policy && policy.isIssued()) {
          postIssueDataPromises.push(fetchSetPostIssueData.call(this, policy, policyInfoPayload));
        }
      }
      await Promise.all(postIssueDataPromises);
    }

    async function fetchSetPostIssueData(policy, policyInfoPayload) {
      let postIssuePayerData, paInvoices, paRecentInvoiceInfo, paCreatedNoAuthInvoiceDate;
      if (this.controller.enableAuthPayforDDB) {
        postIssuePayerData = await this.controller.accountCenterController.getPAPayerInfo(policy);
        paInvoices = await this.controller.accountCenterController.getPAInvoices(policy);
        paCreatedNoAuthInvoiceDate = this.paCreatedNoAuthInvoiceDate(paInvoices);
      }
      if (this.controller.enableFailedPaymentRetry) {
        paRecentInvoiceInfo = await this.extractRecentInvoiceInfo(policy);
      }

      const postIssuePaymentData = this.setPostIssueData(
        policy,
        policyInfoPayload,
        postIssuePayerData,
        paRecentInvoiceInfo,
        paCreatedNoAuthInvoiceDate
      );
      const postIssueData = new PolicyData(policy, postIssuePaymentData);
      postIssueData.overriderPostIssueData(policyInfoPayload);
      this.policyNumberPolicyDataMap.set(policy.__id__, postIssueData);
    }
  }

  async extractRecentInvoiceInfo(policy: CoverpathPolicy | MMDirectPolicy) {
    const paInvoicesInfo = await this.controller.accountCenterController.getPAInvoices(policy);
    if (paInvoicesInfo) {
      paInvoicesInfo.sort((a, b) =>
        Date.parse(a.date) < Date.parse(b.date) ? 1 : Date.parse(a.date) > Date.parse(b.date) ? -1 : 0
      );
      if (paInvoicesInfo.length > 0) {
        return paInvoicesInfo[0];
      }
    }
  }

  extractPaymentFailureDate(invoiceTransactions): Date {
    if (invoiceTransactions) {
      invoiceTransactions.sort((a, b) =>
        Date.parse(a.date) < Date.parse(b.date) ? 1 : Date.parse(a.date) > Date.parse(b.date) ? -1 : 0
      );
      if (invoiceTransactions.length > 0 && invoiceTransactions[0].status === this.PAYMENT_FAILURE_STATUS) {
        return new Date(invoiceTransactions[0].date);
      }
    }
  }

  getHasPaymentFailure(paRecentInvoiceInfo, policy): boolean {
    const actionString = this.makeActionString(policy);
    return (
      paRecentInvoiceInfo?.status === this.PAYMENT_FAILURE_STATUS &&
      policy.isAutoPay &&
      CoverpathUtils.isInForceIssued(actionString)
    );
  }

  paCreatedNoAuthInvoiceDate(paInvoices): boolean {
    let invoiceDate = paInvoices.find((invoice) => ['Created', 'NoAuthorization'].includes(invoice.status))?.date;
    if (invoiceDate) {
      invoiceDate = new Date(invoiceDate);
      return invoiceDate.setDate(invoiceDate.getDate() + 1);
    }
  }

  private setPreIssueDataPerPolicy(currentPolicy, policyId) {
    //Removing annuity
    if (Utils.isCoverpathPlatform(currentPolicy.getChannelCode()) && !currentPolicy.isAnnuity()) {
      this.policyNumberPolicyDataMap.set(policyId, new PolicyData(currentPolicy, this.setPreIssueData(currentPolicy)));
    }
  }

  /**
   *
   * @param policy
   * @returns CACPaymentData
   */
  setPreIssueData(policy: Policy): CACPaymentData {
    const riders: Rider[] = policy.ridersWithPendingAmendment().map((rider) => {
      return {
        enabled: true,
        type: rider.type,
        amount: rider.amount,
        flatExtras: rider.flatExtras,
        premium: rider.premiumBase,
        waiver: rider.premiumWaiver,
        plannedYears: rider.plannedYears,
        hasScheduledPayments: rider.hasScheduledPayments,
        paymentFrequency: rider.paymentFrequency,
        lumpSumUses1035: rider.lumpSumUses1035,
      };
    });

    const exchange1035: QuoteExchange1035 = {
      enabled: false,
      cashValueTransfer: 0,
      costBasis: 0,
      policyType: '',
      applyTowards: '',
      expectedArrival: 0,
      isMec: false,
      dateFundsReceived: null,
    };

    if (policy.hasAlirRiderWithPendingAmendment() && policy.alirRider.hasAlir1035ExchangeWithPendingAmendment()) {
      exchange1035.enabled = policy.alirRider.hasAlir1035ExchangeWithPendingAmendment();
      exchange1035.cashValueTransfer = policy.alirRider.alir1035ExchangeAmountWithPendingAmendment();
      exchange1035.costBasis = policy.alirRider.alir1035CostBasisWithPendingAmendment();
      exchange1035.policyType = policy.alirRider.alir1035PolicyTypeWithPendingAmendment();
      exchange1035.applyTowards = policy.alirRider.alir1035ApplyTowardsWithPendingAmendment();
      exchange1035.expectedArrival = policy.alirRider.alir1035ExpectedArrivalWithPendingAmendment();
      exchange1035.isMec = policy.alirRider.alir1035IsMecWithPendingAmendment();
      exchange1035.dateFundsReceived = policy.alirRider.alir1035FundsReceivedDateWithPendingAmendment();
    }

    return new CACPaymentData(
      policy.faceAmountWithPendingAmendment(),
      policy.hasWaiverWithPendingAmendment(),
      policy.premiumBreakdown.waiverAmountWithPendingAmendment(),
      policy.premiumBreakdown.baseWithPendingPremiumAmendment(),
      riders,
      policy.premiumBreakdown.flatExtrasWithPendingAmendment(),
      policy.paymentFrequencyWithPendingAmendment(),
      policy.paymentMode,
      exchange1035,
      policy.isAutoPay,
      null,
      policy.effectiveDateWithPendingAmendment(),
      null,
      policy.premiumBreakdown.getTotalWithPendingAmendment(),
      policy.premiumBreakdown.getTempFlatExtras(),
      policy.premiumBreakdown.getTotalTempFlatExtrasPremium()
    );
  }

  /**
   *
   * @param policy
   * @param policyInfoPayload
   * @returns CACPaymentData
   * Please note: waiverPremium & basePremium from the policyInfo payload includes waiver & base premium of riders too. Hence use from policy.
   * policyInfo does not include WL rider & flatExtras information.
   */
  setPostIssueData(
    policy: Policy,
    policyInfoPayload: LifePolicyInfoDTO,
    postIssuePayerData,
    paRecentInvoiceInfo,
    paCreatedNoAuthInvoiceDate
  ): CACPaymentData {
    const riders: Rider[] = policy.ridersWithPendingAmendment().map((rider) => {
      return {
        enabled: true,
        type: rider.type,
        amount: rider.amount,
        flatExtras: rider.flatExtras,
        premium: rider.premiumBase,
        waiver: rider.premiumWaiver,
        plannedYears: rider.plannedYears,
        hasScheduledPayments: rider.hasScheduledPayments,
        paymentFrequency: rider.paymentFrequency,
        lumpSumUses1035: rider.lumpSumUses1035,
      };
    });

    const exchange1035: QuoteExchange1035 = {
      enabled: false,
      cashValueTransfer: 0,
      costBasis: 0,
      policyType: '',
      applyTowards: '',
      expectedArrival: 0,
      isMec: false,
      dateFundsReceived: null,
    };
    if (policy.hasAlirRiderWithPendingAmendment() && policy.alirRider.hasAlir1035ExchangeWithPendingAmendment()) {
      exchange1035.enabled = policy.alirRider.hasAlir1035ExchangeWithPendingAmendment();
      exchange1035.cashValueTransfer = policy.alirRider.alir1035ExchangeAmountWithPendingAmendment();
      exchange1035.costBasis = policy.alirRider.alir1035CostBasisWithPendingAmendment();
      exchange1035.policyType = policy.alirRider.alir1035PolicyTypeWithPendingAmendment();
      exchange1035.applyTowards = policy.alirRider.alir1035ApplyTowardsWithPendingAmendment();
      exchange1035.expectedArrival = policy.alirRider.alir1035ExpectedArrivalWithPendingAmendment();
      exchange1035.isMec = policy.alirRider.alir1035IsMecWithPendingAmendment();
      exchange1035.dateFundsReceived = policy.alirRider.alir1035FundsReceivedDateWithPendingAmendment();
    }

    let totalPremiumFromPolicyInfo =
      Number(policyInfoPayload.policyInfo.basePremiumAmount) +
      Number(policyInfoPayload.policyInfo.feeAmount) +
      Number(policyInfoPayload.policyInfo.flatExtraAmount) +
      Number(policyInfoPayload.policyInfo.waiverPremiumAmount) +
      Number(policyInfoPayload.policyInfo.substandardPremiumAmount);
    if (policy.hasAlirRiderWithPendingAmendment()) {
      totalPremiumFromPolicyInfo += policy.alirRider.premiumBaseWithPendingAmendment();
    }

    return new CACPaymentData(
      Number(policyInfoPayload.policyInfo.faceAmount),
      policyInfoPayload.waiverOfPremium as boolean,
      policy.premiumBreakdown.waiverAmountWithPendingAmendment(),
      policy.premiumBreakdown.baseWithPendingPremiumAmendment(),
      riders,
      policy.premiumBreakdown.flatExtrasWithPendingAmendment(),
      policyInfoPayload.paymentFrequency,
      policyInfoPayload.paymentType,
      exchange1035,
      postIssuePayerData?.isAutoPay || policy.isAutoPay,
      this.extractNextPaymentDate(policyInfoPayload.policyInfo.nextPaymentDate, policyInfoPayload.paymentFrequency),
      policyInfoPayload.policyInfo.effectiveDate ? new Date(policyInfoPayload.policyInfo.effectiveDate) : null,
      policyInfoPayload.policyInfo.conversionEndDate ? new Date(policyInfoPayload.policyInfo.conversionEndDate) : null,
      totalPremiumFromPolicyInfo,
      policy.premiumBreakdown.getTempFlatExtras(),
      policy.premiumBreakdown.getTotalTempFlatExtrasPremium(),
      null,
      postIssuePayerData?.nextPaymentAuthorized,
      postIssuePayerData?.paymentInfo.bankAccountNumber?.slice(-4),
      postIssuePayerData?.paymentInfo.accountType,
      postIssuePayerData?.paymentInfo.payerName,
      postIssuePayerData?.paymentInfo.effectiveDraftDate,
      postIssuePayerData?.isBillingPaused,
      this.controller.enableAuthPayforDDB,
      this.getHasPaymentFailure(paRecentInvoiceInfo, policy),
      this.extractPaymentFailureDate(paRecentInvoiceInfo?.invoiceTransactions),
      paRecentInvoiceInfo?.amount,
      paCreatedNoAuthInvoiceDate ? new Date(paCreatedNoAuthInvoiceDate) : null,
      paRecentInvoiceInfo?.invoiceId
    );
  }

  // nextPaymentDate may be a stringified date or an object with frequency
  // as key and stringified date as value
  extractNextPaymentDate(nextPaymentDate, frequency): Date {
    if (nextPaymentDate[frequency]) {
      return new Date(nextPaymentDate[frequency]);
    } else if (nextPaymentDate) {
      return new Date(nextPaymentDate);
    } else {
      return null;
    }
  }

  setPolicyInfo(policy: CoverpathPolicy | MMDirectPolicy, data: CACPaymentData) {
    return new PolicyData(policy, data);
  }

  getPolicyDataFromPolicy(policyId: string) {
    return this.policyNumberPolicyDataMap.get(policyId);
  }

  getClientAccountCenterViewDataFromPolicy(policyId: string) {
    return this.getPolicyDataFromPolicy(policyId).getCACPaymentData();
  }

  // Get most recent 1035 exchange value and status. Returns null when PA is down.
  public async get1035ExchangeData(policy: CoverpathPolicy): Promise<{
    amount: number;
    status: string;
    issueDate1035?: Date;
  } | null> {
    let amount =
      policy.application.primaryPolicyWithPendingAmendment.alirRider.alir1035ExchangeAmountWithPendingAmendment();
    let status, issueDate1035;
    if (policy.alirRider.alir1035ApplyTowardsWithPendingAmendment() === Exchange1035FundingPurpose.PREMIUM_AND_ALIR) {
      // Applied towards first premium then ALIR -- will pause before issuance
      if (!!policy.alirRider.exchange1035.dateFundsReceived) {
        status = Exchange1035FundingStatus.COMPLETED;
      } else if (
        /Funding Pause/.test(policy.workflowState) &&
        !/Initiate Funds Transfer/.test(policy.workflowSubState)
      ) {
        status = Exchange1035FundingStatus.IN_PROGRESS; // FP2+
      } else {
        status = Exchange1035FundingStatus.NOT_STARTED;
      }
    } else {
      // Applied towards ALIR only -- no funding pause
      if (policy.isInForce()) {
        try {
          const payload = await this.amorphicService.controller.getPolicyDetails(policy);
          if (payload.issueDate1035) {
            amount = payload.finalCashValueTransfer;
            issueDate1035 = payload.issueDate1035;
            status = Exchange1035FundingStatus.COMPLETED;
          } else {
            status = Exchange1035FundingStatus.IN_PROGRESS;
          }
        } catch (err) {
          return null;
        }
      } else {
        status = Exchange1035FundingStatus.NOT_STARTED;
      }
    }
    if (status === Exchange1035FundingStatus.NOT_STARTED && !!policy.application.previewOfferAcceptedDate) {
      // If they amend the applyTowards value in FP2+, still show IN_PROGRESS in the later stages as it's
      // been "started" by client already
      status = Exchange1035FundingStatus.IN_PROGRESS;
    }
    return { amount, status, issueDate1035 };
  }

  public async getCashValue(policy: CoverpathPolicy): Promise<CashValueQuote> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'account-center.service',
        function: 'getCashValue',
        category: UICategory.request,
        data: {
          applicationNumber: policy.policyNumber,
        },
      },
      'getCashValue'
    );

    const cashValueQuote: CashValueQuote = await this.controller.accountCenterController.getCashValue(policy);
    return cashValueQuote;
  }
  /**
   *
   * @param staleClientAccountCenterViewData
   * @param paPolicy
   * @param policy
   * @returns CACPaymentData
   */
  setPAPolicyDetails(
    paPolicy,
    policy: CoverpathPolicy | MMDirectPolicy,
    paPayerInfo,
    paRecentInvoiceInfo,
    paCreatedNoAuthInvoiceDate
  ): CACPaymentData {
    const premiumResponse = {
      version: '0',
      base: 0,
      waiver: 0,
      riders: [],
      flatExtras: [],
    };
    let policyEndDate = null;

    paPolicy.activeSegments.forEach((segment) => {
      const segmentPremium = paPolicy.segmentPremiums[segment._id];
      switch (segment.segmentType) {
        case 'PolicySegment':
        case 'FaceIncreaseSegment':
          premiumResponse.base += segmentPremium.benefitPremiums
            .filter((benefitPremium) => benefitPremium.benefitCode === BenefitCode.Death)
            .reduce((acc, benefitPremium) => benefitPremium.premiumAmount + acc, 0);
          premiumResponse.waiver += segmentPremium.benefitPremiums
            .filter((benefitPremium) => benefitPremium.benefitCode === BenefitCode.Waiver)
            .reduce((acc, benefitPremium) => benefitPremium.premiumAmount + acc, 0);
          if (segment.segmentType === 'PolicySegment') {
            premiumResponse.flatExtras = segmentPremium.benefitPremiums
              .filter((benefitPremium) => benefitPremium.benefitType === 'LifeFlatExtra')
              .map((flatExtra) => {
                return {
                  name: flatExtra.benefitCode,
                  premium: flatExtra.premiumAmount,
                  expiryAfterInYears: flatExtra.duration,
                };
              });
            policyEndDate = segment.endDate;
          }
          break;
        case 'RTRSegment': {
          const rider: Rider = {
            enabled: true,
            type: RiderType.RTR,
            amount: segment.segmentBenefits[0].units,
            premium: segmentPremium.benefitPremiums
              .filter((benefitPremium) => benefitPremium.benefitCode === BenefitCode.Death)
              .reduce((acc, benefitPremium) => benefitPremium.premiumAmount + acc, 0),
            waiver: segmentPremium.benefitPremiums
              .filter((benefitPremium) => benefitPremium.benefitCode === BenefitCode.Waiver)
              .reduce((acc, benefitPremium) => benefitPremium.premiumAmount + acc, 0),
            flatExtras: segmentPremium.benefitPremiums
              .filter((benefitPremium) => benefitPremium.benefitType === 'LifeFlatExtra')
              .map((flatExtra) => {
                return {
                  name: flatExtra.benefitCode,
                  premium: flatExtra.premiumAmount,
                  expiryAfterInYears: flatExtra.duration,
                };
              }),
          };
          premiumResponse.riders.push(rider);
          break;
        }
        case 'GIRSegment': {
          const rider: Rider = {
            enabled: true,
            type: RiderType.GIR,
            amount: segment.segmentBenefits[0].units,
            premium: segmentPremium.benefitPremiums
              .filter((benefitPremium) => benefitPremium.benefitCode === BenefitCode.GIR)
              .reduce((acc, benefitPremium) => benefitPremium.premiumAmount + acc, 0),
            waiver: segmentPremium.benefitPremiums
              .filter((benefitPremium) => benefitPremium.benefitCode === BenefitCode.Waiver)
              .reduce((acc, benefitPremium) => benefitPremium.premiumAmount + acc, 0),
            flatExtras: [],
          };
          premiumResponse.riders.push(rider);
          break;
        }
      }
    });

    if (policy.hasAlirRiderWithPendingAmendment()) {
      premiumResponse.riders.push({
        enabled: true,
        type: RiderType.ALIR,
        amount: policy.alirRider.amountWithPendingAmendment(),
        plannedYears: policy.alirRider.plannedYearsWithPendingAmendment(),
        paymentFrequency: policy.alirRider.paymentFrequencyWithPendingAmendment(),
        premium: policy.alirRider.premiumBaseWithPendingAmendment(),
        waiver: policy.alirRider.premiumWaiverWithPendingAmendment(),
        hasScheduledPayments: policy.alirRider.hasAlirScheduledPaymentsWithPendingAmendment(),
        flatExtras: [],
        lumpSumUses1035: policy.alirRider.lumpSumUses1035,
      });
    }

    const exchange1035: QuoteExchange1035 = {
      enabled: false,
      cashValueTransfer: 0,
      costBasis: 0,
      policyType: '',
      applyTowards: '',
      expectedArrival: 0,
      isMec: false,
      dateFundsReceived: null,
    };
    if (policy.hasAlirRiderWithPendingAmendment() && policy.alirRider.hasAlir1035ExchangeWithPendingAmendment()) {
      exchange1035.enabled = policy.alirRider.hasAlir1035ExchangeWithPendingAmendment();
      exchange1035.cashValueTransfer = policy.alirRider.alir1035ExchangeAmountWithPendingAmendment();
      exchange1035.costBasis = policy.alirRider.alir1035CostBasisWithPendingAmendment();
      exchange1035.policyType = policy.alirRider.alir1035PolicyTypeWithPendingAmendment();
      exchange1035.applyTowards = policy.alirRider.alir1035ApplyTowardsWithPendingAmendment();
      exchange1035.expectedArrival = policy.alirRider.alir1035ExpectedArrivalWithPendingAmendment();
      exchange1035.isMec = policy.alirRider.alir1035IsMecWithPendingAmendment();
      exchange1035.dateFundsReceived = policy.alirRider.alir1035FundsReceivedDateWithPendingAmendment();
    }

    if (policy.isCoverpathPolicy() && (policy as CoverpathPolicy).hasCirRiderWithPendingAmendment()) {
      const cirRider = (policy as CoverpathPolicy).cirRider;
      premiumResponse.riders.push({
        enabled: true,
        type: RiderType.CIR,
        amount: cirRider.amountWithPendingAmendment(),
        premium: cirRider.premiumBaseWithPendingAmendment(),
        waiver: cirRider.premiumWaiverWithPendingAmendment(),
        benefitDisbursement: cirRider.benefitDisbursementMethodWithPendingAmendment(),
        puaAddedToBenefitPool: cirRider.puaAddedToBenefitPoolWithPendingAmendment(),
        monthlyIncreaseOption: cirRider.monthlyIncreaseOptionWithPendingAmendment(),
        predictedBenefitAge: cirRider.predictedBenefitAgeWithPendingAmendment(),
      });
    }

    let totalPremiumFromPolicyInfo = paPolicy.paymentInfo.paymentAmount[paPolicy.paymentInfo.frequency];
    if (policy.hasAlirRiderWithPendingAmendment()) {
      totalPremiumFromPolicyInfo += policy.alirRider.premiumBaseWithPendingAmendment();
    }

    return new CACPaymentData(
      Number(paPolicy.faceAmount),
      premiumResponse.waiver > 0,
      premiumResponse.waiver,
      premiumResponse.base,
      premiumResponse.riders,
      premiumResponse.flatExtras,
      paPolicy.paymentInfo.frequency,
      paPolicy.paymentInfo.paymentMethodType,
      exchange1035,
      paPayerInfo?.isAutoPay || policy.isAutoPay,
      this.extractNextPaymentDate(paPolicy.paymentInfo.nextPaymentDate, paPolicy.paymentInfo.frequency),
      paPolicy.effectiveDate ? new Date(paPolicy.effectiveDate) : null,
      paPolicy.conversionPeriodInfo.endDate ? new Date(paPolicy.conversionPeriodInfo.endDate) : null,
      totalPremiumFromPolicyInfo,
      policy.premiumBreakdown.getTempFlatExtras(),
      policy.premiumBreakdown.getTotalTempFlatExtrasPremium(),
      policyEndDate,
      paPayerInfo?.nextPaymentAuthorized,
      paPayerInfo?.paymentInfo.bankAccountNumber?.slice(-4),
      paPayerInfo?.paymentInfo.accountType,
      paPayerInfo?.paymentInfo.payerName,
      paPayerInfo?.paymentInfo.effectiveDraftDate,
      paPayerInfo?.isBillingPaused,
      this.controller.enableAuthPayforDDB,
      this.getHasPaymentFailure(paRecentInvoiceInfo, policy),
      this.extractPaymentFailureDate(paRecentInvoiceInfo?.invoiceTransactions),
      paRecentInvoiceInfo?.amount,
      paCreatedNoAuthInvoiceDate ? new Date(paCreatedNoAuthInvoiceDate) : null,
      paRecentInvoiceInfo?.invoiceId
    );
  }
}
